import { getLoginUrl } from "@/const";
import { apiClient } from "@/lib/trpc";
import { TRPCClientError } from "@trpc/client";
import { useCallback, useEffect, useMemo, useState } from "react";

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

type AuthUser = Awaited<ReturnType<typeof apiClient.auth.me.query>>;

export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false, redirectPath = getLoginUrl() } = options ?? {};

  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const me = await apiClient.auth.me.query();
      setUser(me ?? null);
      setError(null);
      localStorage.setItem("manus-runtime-user-info", JSON.stringify(me ?? null));
      return me ?? null;
    } catch (nextError) {
      setError(nextError);
      setUser(null);
      localStorage.setItem("manus-runtime-user-info", "null");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    setLoggingOut(true);

    try {
      await apiClient.auth.logout.mutate();
    } catch (nextError: unknown) {
      if (nextError instanceof TRPCClientError && nextError.data?.code === "UNAUTHORIZED") {
        return;
      }
      throw nextError;
    } finally {
      setUser(null);
      localStorage.setItem("manus-runtime-user-info", "null");
      setLoggingOut(false);
      setLoading(false);
    }
  }, []);

  const state = useMemo(
    () => ({
      user,
      loading: loading || loggingOut,
      error,
      isAuthenticated: Boolean(user),
    }),
    [error, loading, loggingOut, user],
  );

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (state.loading) return;
    if (state.user) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === redirectPath) return;

    window.location.href = redirectPath;
  }, [redirectOnUnauthenticated, redirectPath, state.loading, state.user]);

  return {
    ...state,
    refresh,
    logout,
  };
}
